/**
 * executiveSummaryRail — the KPI-rail resolver for the Executive Summary
 * locked layout (D1, "Verdict-led"). It maps the SAME null-guarded cover
 * chip set (`resolveCoverChips`) the cover variants already consume into
 * the `EditorialKpiItem[]` the editorial KPI rail renders.
 *
 * WHY REUSE THE COVER CHIPS: the cover's `resolveCoverChips(report)` is the
 * established, audited chain that turns intent + report fallbacks into the
 * exact metrics the D1 rail wants (Power · LCOE · BTM · Cooling), each
 * already returning `null` when the underlying intent/report value is
 * absent. Reusing it means the Exec-Summary rail can NEVER drift from the
 * cover chips and — per the no-fabrication rule — only shows a KPI when the
 * canonical resolver decided a real value exists. An undecided metric is
 * DROPPED by `EditorialKpiRail` (it filters null values), so an
 * intent-light report renders a shorter rail or no rail at all, never a
 * placeholder number.
 *
 * The verdict itself is NOT in this rail — the D1 layout renders the verdict
 * as the section hero (a separate `EditorialVerdictMark`), wired from the
 * renderer's existing `getDisplayVerdict(report)` result.
 *
 * Pure / synchronous / null-safe (delegates to the cover resolver, which
 * never throws). Mirrors the D1 rail order: Power → LCOE → Phases/BTM → COD.
 */
import type { SiteRagReport } from '../../../types/siteRag';
import type { EditorialKpiItem } from './editorial';
import { resolveCoverChips, type CoverChip } from '../../../lib/reportRendering/coverKpiChips';
import { TIMELINE_BUCKET_LABEL, effectivePhases } from '../../../lib/projectIntent';

/** The signature hero metric for the Executive Summary lead-in (K6). */
export interface ExecutiveSummaryHero {
  /** The chip id the hero was promoted from (so the rail can drop it). */
  fromChipId: CoverChip['id'];
  /** Wide-tracked mono small-caps label, e.g. "Power Target". */
  label: string;
  /** Serif display numeral, e.g. "300". */
  value: string;
  /** Small mono unit riding the numeral baseline, e.g. "MW". */
  unit?: string;
}

/**
 * Split a resolved chip value into a serif-numeral hero `{ value, unit }` ONLY
 * when it is a clean single-figure metric — a leading number (optionally with a
 * `$`/`±` prefix and decimals) followed by a short unit token (`MW`, `MVA`,
 * `/MWh`, `ac`, `%`, `km`, …). Compound values ("300 MW · 3 phases", "Est. 250
 * MW (HIFLD proxy)", prose) return `null` so the caller keeps them in the rail
 * rather than fabricating a hero out of a multi-part string. This never invents
 * a number — it only re-presents an already-resolved clean one.
 */
function splitHeroValue(raw: string): { value: string; unit?: string } | null {
  const trimmed = raw.trim();
  // Reject anything carrying a separator / estimate qualifier / prose — those
  // are multi-part chips, not a single hero numeral.
  if (/[·,()]|\bEst\.\b|→|phases?\b/i.test(trimmed)) return null;

  // $NN/MWh  → value "$NN", unit "/MWh"
  const lcoe = /^(\$?-?[\d.]+)\s*(\/[A-Za-z]+)$/.exec(trimmed);
  if (lcoe) return { value: lcoe[1], unit: lcoe[2] };

  // ±$NNM / $NN.NB (compact currency) — keep whole, no unit split.
  if (/^[+\-−]?\$[\d.]+[MB]?$/.test(trimmed)) return { value: trimmed };

  // NN unit  → value "NN", unit "unit" (MW / MVA / ac / km / GW …)
  const numUnit = /^(-?[\d.]+)\s*([A-Za-z%][A-Za-z/%]{0,4})$/.exec(trimmed);
  if (numUnit) return { value: numUnit[1], unit: numUnit[2] };

  // Bare percentage "NN/100" (sentiment index) — keep whole.
  if (/^-?[\d.]+\/100$/.test(trimmed)) return { value: trimmed };

  return null;
}

/**
 * Resolve the single signature hero metric for the Executive Summary lead-in
 * (K6). Reuses the SAME canonical cover-chip chain as the rail (no new data
 * path, no fabrication): the FIRST resolved chip — Power for most types, the
 * verdict/LCOE for executive-summary/financial — is the type's primary metric.
 * Returns `null` when that primary chip is absent or is a compound / prose
 * value that does not split into a clean serif numeral, in which case the rail
 * alone carries it. The hero is intentionally a SINGLE dominant numeral (the
 * redesign leads with one), with the remaining metrics in the rail beneath.
 */
export function resolveExecutiveSummaryHero(report: SiteRagReport): ExecutiveSummaryHero | null {
  const chips = resolveCoverChips(report);
  const primary = chips.find((c) => c.value != null && c.value.trim().length > 0);
  if (!primary?.value) return null;

  const split = splitHeroValue(primary.value);
  if (!split) return null;

  const label = primary.label?.trim() || 'Primary metric';
  return { fromChipId: primary.id, label, value: split.value, unit: split.unit };
}

/** Read the gen-time intent snapshot (preferred) else the soft top-level cast. */
function readIntent(report: SiteRagReport) {
  return (
    report.projectIntentAtGen
    ?? (report as { projectIntent?: import('../../../types/siteRag').ProjectIntent }).projectIntent
    ?? undefined
  );
}

/** A short COD / phasing KPI from the effective phases view — `null` when
 *  the report carries no decided phasing (drops the cell). */
function resolveCodItem(report: SiteRagReport): EditorialKpiItem | null {
  const phases = effectivePhases(readIntent(report));
  if (phases.ultimateInServiceDate) {
    return { key: 'Target COD', value: phases.ultimateInServiceDate };
  }
  if (phases.initialInServiceDate && phases.ultimateCodTimeline) {
    const finalPhaseLabel = phases.phases[phases.phases.length - 1]?.label || `Phase ${phases.phaseCount}`;
    return {
      key: 'Phase 1 COD / Target',
      value: `${phases.initialInServiceDate} · ${finalPhaseLabel} ${TIMELINE_BUCKET_LABEL[phases.ultimateCodTimeline]} (planning target, not a COD)`,
    };
  }
  if (phases.initialInServiceDate) {
    if (phases.isPhased && phases.phaseCount > 1) {
      const finalPhaseLabel = phases.phases[phases.phases.length - 1]?.label || `Phase ${phases.phaseCount}`;
      return {
        key: 'Phase 1 COD / Target',
        value: `${phases.initialInServiceDate} · ${finalPhaseLabel} COD TBD`,
      };
    }
    return { key: 'Target COD', value: phases.initialInServiceDate };
  }
  return null;
}

/** A "Phases" KPI from the effective phases view — `null` for single-phase /
 *  undecided so it drops out of the rail. */
function resolvePhasesItem(report: SiteRagReport): EditorialKpiItem | null {
  const phases = effectivePhases(readIntent(report));
  if (phases.isPhased && phases.phaseCount > 1) {
    return { key: 'Phases', value: String(phases.phaseCount) };
  }
  return null;
}

/**
 * Build the Executive Summary KPI rail items in D1 order. Each item is
 * sourced from the canonical cover-chip resolver (Power / LCOE) or the
 * effective-phases view (Phases / COD). Items with no real value are
 * omitted here AND filtered again by EditorialKpiRail, so the rail is
 * never padded with fabricated numbers.
 *
 * K6 — when a chip has been promoted to the signature hero numeral, pass its
 * id as `excludeChipId` so the rail drops it (no duplicate of the hero metric
 * in the rail beneath).
 */
export function resolveExecutiveSummaryRail(
  report: SiteRagReport,
  excludeChipId?: CoverChip['id'],
): EditorialKpiItem[] {
  const chips = resolveCoverChips(report);
  const byId = new Map(chips.map((c) => [c.id, c]));

  const items: Array<EditorialKpiItem | null> = [];

  const power = byId.get('power');
  if (power?.value && excludeChipId !== 'power') {
    items.push({ key: power.label || 'Power target', value: power.value });
  }

  const lcoe = byId.get('lcoe');
  if (lcoe?.value && excludeChipId !== 'lcoe') {
    items.push({ key: lcoe.label || 'LCOE', value: lcoe.value });
  }

  items.push(resolvePhasesItem(report));
  items.push(resolveCodItem(report));

  return items.filter((it): it is EditorialKpiItem => it != null);
}
